import { ReactNode, useRef } from 'react';
import { A11y, Mousewheel, Navigation } from 'swiper';
import { Swiper, SwiperSlide, SwiperSlideProps } from 'swiper/react';

import { type IconButtonProps, Box, Flex, IconButton } from '@chakra-ui/react';

import { ChevronLeftIcon, ChevronRightIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';

import 'swiper/css';
import 'swiper/css/mousewheel';
import 'swiper/css/navigation';

export type CardCarouselSlide = {
  key: string;
  content: ReactNode;
  slideProps?: SwiperSlideProps;
};

export type CardCarouselProps = {
  slides: CardCarouselSlide[];
  slidesPerView?: number;
  spaceBetween?: number;
  maxWidth?: string;
  innerMaxWidth?: string;
  navButtonColor?: string;
  navButtonHoverBg?: string;
  navButtonActiveBg?: string;
  navButtonOpacity?: number;
  showNavButtons?: IconButtonProps['display'];
  onSlideNext?: () => void;
  onSlidePrev?: () => void;
  onSlideMove?: () => void;
};

export function CardCarousel({
  slides,
  slidesPerView = 4,
  spaceBetween = 24,
  maxWidth = '1240px',
  innerMaxWidth = '1096px',
  navButtonColor,
  navButtonHoverBg,
  navButtonActiveBg,
  navButtonOpacity = 0.5,
  showNavButtons = { base: 'none', xl: 'block' },
  onSlideNext,
  onSlidePrev,
  onSlideMove,
}: CardCarouselProps) {
  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const isClientReady = useClientRender();
  const { i18n, isRtl } = useI18n();

  return (
    <Flex alignItems='center' flexDirection='row' gap={6} margin='0 auto' maxWidth={maxWidth} position='relative'>
      <Box
        isolation='isolate'
        margin='0 auto'
        maxWidth={innerMaxWidth}
        width='100%'
        sx={{
          '.swiper .swiper-slide': {
            height: 'auto',
            display: 'flex',
          },
        }}
      >
        <Swiper
          key={isClientReady ? 'client' : 'server'}
          centeredSlides={false}
          direction='horizontal'
          modules={[A11y, Mousewheel, Navigation]}
          slidesPerView={slidesPerView}
          spaceBetween={spaceBetween}
          mousewheel={{
            forceToAxis: true,
            thresholdDelta: 10,
          }}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper) => {
            swiper.changeLanguageDirection(i18n.dir());
            // The arrow refs are null on first render (the IconButtons mount
            // after Swiper in the tree), so the `navigation` prop above can't
            // see them. Reassign here, once the refs are attached, so the
            // desktop prev/next controls actually drive the carousel.
            if (typeof swiper.params.navigation !== 'boolean' && swiper.params.navigation) {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
          }}
          onNavigationNext={() => {
            onSlideNext?.();
          }}
          onNavigationPrev={() => {
            onSlidePrev?.();
          }}
          onSliderMove={() => {
            onSlideMove?.();
          }}
        >
          {slides.map((slide) => (
            <SwiperSlide key={slide.key} {...slide.slideProps}>
              {slide.content}
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
      <IconButton
        ref={prevRef}
        _active={navButtonActiveBg ? { bg: navButtonActiveBg } : undefined}
        _hover={navButtonHoverBg ? { bg: navButtonHoverBg } : undefined}
        aria-label='Previous'
        color={navButtonColor}
        display={showNavButtons}
        left='-60px'
        position='absolute'
        size='lg'
        variant='ghost'
        icon={
          isRtl ? (
            <ChevronRightIcon height='48px' opacity={navButtonOpacity} width='48px' />
          ) : (
            <ChevronLeftIcon height='48px' opacity={navButtonOpacity} width='48px' />
          )
        }
      />
      <IconButton
        ref={nextRef}
        _active={navButtonActiveBg ? { bg: navButtonActiveBg } : undefined}
        _hover={navButtonHoverBg ? { bg: navButtonHoverBg } : undefined}
        aria-label='Next'
        color={navButtonColor}
        display={showNavButtons}
        position='absolute'
        right='-60px'
        size='lg'
        variant='ghost'
        icon={
          isRtl ? (
            <ChevronLeftIcon height='48px' opacity={navButtonOpacity} width='48px' />
          ) : (
            <ChevronRightIcon height='48px' opacity={navButtonOpacity} width='48px' />
          )
        }
      />
    </Flex>
  );
}
